/**
 * AppHeader Synchronizer
 *
 * This module provides bidirectional synchronization between the Angular AppHeaderViewModel
 * and the Redux appHeader state. It ensures that whichever version of the AppHeader is
 * rendered (Angular or React), all state changes propagate correctly to both systems.
 *
 * Key concepts:
 * - Origin tracking: Prevents infinite sync loops by tracking whether a change originated
 *   from Angular or Redux
 * - Property interception: Angular view model setters call syncFromAngular()
 * - Middleware integration: Redux middleware calls syncFromRedux()
 */

import { type EnhancedStore } from '@reduxjs/toolkit';
import { appHeaderActions, type AppHeaderReduxState } from '../redux/appHeaderSlice';
import { type PlayerViewModelData, type TitleOpts, type ViewedStream } from '../types';

// Type for the Angular AppHeaderViewModel instance
// oxlint-disable-next-line @typescript-eslint/consistent-type-definitions, quantic/no-type-exports
export interface AppHeaderViewModel {
    // Internal values (accessed directly to bypass setters during sync)
    _hidden?: boolean;
    _hasTopMessage?: boolean;
    _showCaret?: boolean;
    _showAlternateHomeButton?: boolean;
    _title?: string;
    _titleOpts?: TitleOpts;
    _viewedStream?: ViewedStream | null;
    _mobileKeyboardShowing?: boolean;

    // Public getters/setters (we use internal values to avoid triggering setters)
    hidden?: boolean;
    hasTopMessage?: boolean;
    showCaret?: boolean;
    showAlternateHomeButton: boolean;
    title?: string;
    titleOpts?: TitleOpts;
    viewedStream?: ViewedStream | null;
    mobileKeyboardShowing?: boolean;
}

// Property names that are synchronized between Angular and Redux
// oxlint-disable-next-line quantic/no-type-exports
export type SyncedProperty =
    | 'hidden'
    | 'hasTopMessage'
    | 'showCaret'
    | 'showAlternateHomeButton'
    | 'title'
    | 'titleOpts'
    | 'viewedStream'
    | 'mobileKeyboardShowing';

// Origin flag to prevent infinite sync loops
type SyncOrigin = 'angular' | 'redux' | null;

class AppHeaderSynchronizer {
    private store: EnhancedStore | null = null;
    private viewModel: AppHeaderViewModel | null = null;
    private origin: SyncOrigin = null;
    private initialized = false;

    /**
     * Initialize the synchronizer with the Redux store.
     * Called from initializeAppStore after store creation.
     */
    initializeStore(store: EnhancedStore): void {
        this.store = store;
        // If viewModel was registered before store, perform initial sync now
        if (this.viewModel) {
            this.performInitialSync();
        }
    }

    /**
     * Register the Angular AppHeaderViewModel instance.
     * Called from the Angular factory after creating the singleton.
     */
    registerViewModel(viewModel: AppHeaderViewModel): void {
        this.viewModel = viewModel;
        this.initialized = true;

        // Perform initial sync from Angular to Redux to ensure Redux reflects current Angular state
        this.performInitialSync();
    }

    /**
     * Check if the synchronizer is fully initialized
     */
    isInitialized(): boolean {
        return this.initialized && this.store !== null && this.viewModel !== null;
    }

    /**
     * Get the current sync origin.
     * Used by middleware to check if it should skip syncing.
     */
    getOrigin(): SyncOrigin {
        return this.origin;
    }

    /**
     * Sync a property change from Angular to Redux.
     * Called from Angular view model setters.
     */
    syncFromAngular<T>(property: SyncedProperty, value: T): void {
        if (!this.store || this.origin === 'redux') {
            // Skip if store not available or if this change originated from Redux
            return;
        }

        this.origin = 'angular';
        try {
            this.dispatchReduxAction(property, value);
        } finally {
            this.origin = null;
        }
    }

    /**
     * Sync a property change from Redux to Angular.
     * Called from the Redux middleware.
     */
    syncFromRedux<T>(property: SyncedProperty, value: T): void {
        if (!this.viewModel || this.origin === 'angular') {
            // Skip if viewModel not available or if this change originated from Angular
            return;
        }

        this.origin = 'redux';
        try {
            this.updateAngularProperty(property, value);
        } finally {
            this.origin = null;
        }
    }

    /**
     * Dispatch a Redux action for the given property change.
     */
    private dispatchReduxAction<T>(property: SyncedProperty, value: T): void {
        if (!this.store) return;

        switch (property) {
            case 'hidden':
                // toggleVisibility takes the opposite of hidden
                this.store.dispatch(appHeaderActions.toggleVisibility(!(value as boolean)));
                break;
            case 'hasTopMessage':
                this.store.dispatch(appHeaderActions.setHasTopMessage(value as boolean));
                break;
            case 'showCaret':
                this.store.dispatch(appHeaderActions.setShowCaret(value as boolean));
                break;
            case 'showAlternateHomeButton':
                this.store.dispatch(appHeaderActions.setShowAlternateHomeButton(value as boolean));
                break;
            case 'title':
                this.store.dispatch(appHeaderActions.setTitle(value as string | undefined));
                break;
            case 'titleOpts':
                this.store.dispatch(appHeaderActions.setTitleOpts(value as TitleOpts));
                break;
            case 'viewedStream':
                this.store.dispatch(appHeaderActions.setViewedStream(value as ViewedStream | null));
                break;
            case 'mobileKeyboardShowing':
                this.store.dispatch(appHeaderActions.setMobileKeyboardShowing(value as boolean));
                break;
            default:
                // All synced properties should be handled above
                break;
        }
    }

    /**
     * Update the Angular view model property directly (bypassing setters).
     */
    private updateAngularProperty<T>(property: SyncedProperty, value: T): void {
        if (!this.viewModel) return;

        switch (property) {
            case 'hidden':
                this.viewModel._hidden = value as boolean;
                break;
            case 'hasTopMessage':
                this.viewModel._hasTopMessage = value as boolean;
                break;
            case 'showCaret':
                this.viewModel._showCaret = value as boolean;
                break;
            case 'showAlternateHomeButton':
                // showAlternateHomeButton doesn't have underscore prefix in Angular
                this.viewModel.showAlternateHomeButton = value as boolean;
                break;
            case 'title':
                this.viewModel._title = value as string | undefined;
                break;
            case 'titleOpts':
                this.viewModel._titleOpts = value as TitleOpts;
                break;
            case 'viewedStream':
                this.viewModel._viewedStream = value as ViewedStream | null;
                break;
            case 'mobileKeyboardShowing':
                this.viewModel._mobileKeyboardShowing = value as boolean;
                break;
            default:
                // All synced properties should be handled above
                break;
        }
    }

    /**
     * Sync playerViewModel data from Angular to Redux.
     * Called from Angular when playerViewModel changes (set or cleared).
     * This is a one-way sync - React only reads this data, doesn't modify it.
     */
    syncPlayerViewModel(data: PlayerViewModelData | null): void {
        if (!this.store) return;

        this.store.dispatch(appHeaderActions.setPlayerViewModel(data));
    }

    /**
     * Update specific playerViewModel properties without replacing the whole object.
     * Called from Angular when individual properties change (e.g., frameInstructions).
     */
    updatePlayerViewModel(updates: Partial<PlayerViewModelData>): void {
        if (!this.store) return;

        this.store.dispatch(appHeaderActions.updatePlayerViewModel(updates));
    }

    /**
     * Perform initial sync from Angular to Redux on initialization.
     * This ensures Redux state reflects the current Angular state.
     */
    private performInitialSync(): void {
        if (!this.viewModel || !this.store) return;

        const state = this.store.getState() as { appHeader?: AppHeaderReduxState };
        const reduxState = state.appHeader;

        if (!reduxState) return;

        // Only sync if Angular has different values
        // Note: hidden in Redux is the opposite of visibility
        if (this.viewModel._hidden !== undefined && this.viewModel._hidden !== reduxState.hidden) {
            this.syncFromAngular('hidden', this.viewModel._hidden);
        }
        if (this.viewModel._hasTopMessage !== undefined && this.viewModel._hasTopMessage !== reduxState.hasTopMessage) {
            this.syncFromAngular('hasTopMessage', this.viewModel._hasTopMessage);
        }
        if (this.viewModel._showCaret !== undefined && this.viewModel._showCaret !== reduxState.showCaret) {
            this.syncFromAngular('showCaret', this.viewModel._showCaret);
        }
        if (this.viewModel.showAlternateHomeButton !== reduxState.showAlternateHomeButton) {
            this.syncFromAngular('showAlternateHomeButton', this.viewModel.showAlternateHomeButton);
        }
        if (this.viewModel.title !== reduxState.title) {
            this.syncFromAngular('title', this.viewModel.title);
        }
        // Also sync titleOpts, viewedStream, and mobileKeyboardShowing to prevent stale values
        if (this.viewModel._titleOpts !== undefined) {
            this.syncFromAngular('titleOpts', this.viewModel._titleOpts);
        }
        if (this.viewModel._viewedStream !== undefined && this.viewModel._viewedStream !== reduxState.viewedStream) {
            this.syncFromAngular('viewedStream', this.viewModel._viewedStream);
        }
        if (
            this.viewModel._mobileKeyboardShowing !== undefined &&
            this.viewModel._mobileKeyboardShowing !== reduxState.mobileKeyboardShowing
        ) {
            this.syncFromAngular('mobileKeyboardShowing', this.viewModel._mobileKeyboardShowing);
        }
    }
}

// Export singleton instance
export const appHeaderSynchronizer = new AppHeaderSynchronizer();
